import { Badge, createStyles, Group, Loader, Text } from '@mantine/core';
import { first, keys, size } from 'lodash/fp';
import React, { useMemo } from 'react';

import { StoreListing } from '@portals/api/partners';
import { ProductCard } from '@portals/framework';
import { ProductPricingModel } from '@portals/types';
import { getPricingModelDisplayName } from '@portals/utils';

import { useStoreListingPeriodPriceTitle } from '../../hooks/store-listings.hooks';
import { PaymentSettingsMapKeys } from '../../types/store-listings.types';
import { mapStoreListingPricesToPaymentSettings } from '../../utils/store-listings.utils';

interface StoreListingProductCardProps {
  product: StoreListing['product'] | undefined;
  paymentSettings: ReturnType<typeof mapStoreListingPricesToPaymentSettings>;
  className?: string;
}

export function StoreListingProductCard({
  product,
  paymentSettings,
  className,
}: StoreListingProductCardProps) {
  const { classes, cx } = useStyles();
  const { getPriceTitle, isLoadingStoreSettings } =
    useStoreListingPeriodPriceTitle();

  const priceTitle = useMemo(() => {
    if (product?.pricing_model === ProductPricingModel.Personalized) {
      return getPricingModelDisplayName(product?.pricing_model);
    }

    const firstPricePeriod = first(keys(paymentSettings)) as
      | PaymentSettingsMapKeys
      | undefined;

    if (!firstPricePeriod) return null;

    return getPriceTitle({
      period: firstPricePeriod,
      paymentSettings,
      product: product,
    });
  }, [getPriceTitle, paymentSettings, product]);

  return (
    <ProductCard className={cx(classes.card, className)}>
      <ProductCard.Image
        imageUrl={product?.image_url}
        category={product?.category}
      />

      <ProductCard.Details
        name={product?.name ?? ''}
        subtitle={product?.subtitle}
      >
        <Group position="apart">
          {isLoadingStoreSettings ? (
            <Loader size="sm" />
          ) : (
            <Text size="xl" weight={600}>
              {priceTitle}
            </Text>
          )}

          <ExtraPricesBadge numOfPricingPeriods={size(paymentSettings)} />
        </Group>
      </ProductCard.Details>
    </ProductCard>
  );
}

const useStyles = createStyles(() => ({
  card: {
    width: '100%',
    maxWidth: 370,
    filter: 'drop-shadow(0px 11px 18px rgba(6, 72, 105, 0.05))',
  },
}));

function ExtraPricesBadge({
  numOfPricingPeriods,
}: {
  numOfPricingPeriods: number;
}) {
  if (numOfPricingPeriods <= 1) {
    return null;
  }

  const getBadgeText = () => {
    let text = `${numOfPricingPeriods - 1} more`;
    text += numOfPricingPeriods > 2 ? ' prices' : ' price';

    return text;
  };

  return (
    <Badge color="blue_gray" size="md">
      {getBadgeText()}
    </Badge>
  );
}
